import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { sendApiRequest } from '../utils';
import { useSelector } from 'react-redux';

const NetworkExplorer = () => {
  const userData = useSelector((state) => state.user_info[0]?.user);

  // State
  const [expandedNodes, setExpandedNodes] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [networkData, setNetworkData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allExpanded, setAllExpanded] = useState(false);
  const [networkStats, setNetworkStats] = useState({
    totalMembers: 0,
    directReferrals: 0,
    yourSponsor: '-',
    yourRank: '-',
  });

  // Fetch network data
  useEffect(() => {
    const fetchData = async () => {
      if (!userData?._id) {
        setError('User ID not found');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await sendApiRequest(`api/admin/partners/${userData._id}`, {}, 'GET');

        if (response.data?.success) {
          const data = response.data.data;

          // Create network tree structure with recursive children
          const processMLMNode = (mlmNode) => {
            if (!mlmNode) return null;
            
            return {
              id: mlmNode.user._id,
              name: mlmNode.user.fullName || 'Unknown',
              sponsorCode: mlmNode.user.sponsorCode || '-',
              rank: mlmNode.user.rank || '-',
              phone: mlmNode.user.phone || '-',
              email: mlmNode.user.email || '-',
              city: mlmNode.user.city || '-',
              children: mlmNode.children.map(child => processMLMNode(child)) || []
            };
          };

          const networkTree = {
            id: data._id,
            name: data.fullName || 'Unknown',
            sponsorCode: data.sponsorCode || '-',
            city: data.city || '-',
            state: data.state || '-',
            country: data.country || '-',
            rank: data.rank || '-',
            phone: data.phone || '-',
            email: data.email || '-',
            parent: data.mlmTree?.parent
              ? {
                  id: data.mlmTree.parent._id,
                  name: data.mlmTree.parent.fullName || 'Unknown',
                  sponsorCode: data.mlmTree.parent.sponsorCode || '-',
                  rank: data.mlmTree.parent.rank || '-',
                  phone: data.mlmTree.parent.phone || '-',
                  email: data.mlmTree.parent.email || '-',
                }
              : null,
            children: data.mlmTree?.children.map(child => processMLMNode(child)) || []
          };

          // Calculate total members recursively including all levels
          const countMembers = (node) => {
            let count = 1; // Count the current node
            if (node.children) {
              count += node.children.reduce((sum, child) => sum + countMembers(child), 0);
            }
            return count;
          };

          setNetworkData(networkTree);

          // Update network stats with complete tree count
          setNetworkStats({
            totalMembers: countMembers(networkTree),
            directReferrals: networkTree.children.length,
            yourSponsor: data.sponsor || '-',
            yourRank: networkTree.rank || '-',
          });
        } else {
          const message = response.data?.message || 'Failed to fetch network data';
          setError(message);
          toast.error(message);
        }
      } catch (err) {
        const message = err.message || 'Failed to load network data';
        setError(message);
        toast.error(message);
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userData?._id]);

  // Toggle node expansion
  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  // Toggle all nodes
  const toggleAll = () => {
    if (allExpanded) {
      setExpandedNodes({});
    } else {
      const newExpanded = {};
      const expandAllNodes = (node) => {
        if (!node) return;
        newExpanded[node.id] = true;
        node.children?.forEach(expandAllNodes);
      };
      expandAllNodes(networkData);
      setExpandedNodes(newExpanded);
    }
    setAllExpanded(!allExpanded);
  };

  // Helper function to check if a node or its children match the search term
  const matchesSearch = (node, term) => {
    if (!term || !node) return true;

    const termLower = term.toLowerCase();
    return (
      (node.name || '').toLowerCase().includes(termLower) ||
      (node.sponsorCode || '').toLowerCase().includes(termLower) ||
      (node.rank || '').toLowerCase().includes(termLower) ||
      (node.email || '').toLowerCase().includes(termLower) ||
      (node.phone || '').toLowerCase().includes(termLower) ||
      (node.city || '').toLowerCase().includes(termLower) ||
      node.children?.some((child) => matchesSearch(child, term))
    );
  };

  // Render network row
  const renderNetworkRow = (node, level = 0) => {
    if (!node || !matchesSearch(node, searchTerm)) return null;

    const isExpanded = expandedNodes[node.id];
    const hasChildren = node.children && node.children.length > 0;
    const paddingLeft = level * 2;

    return (
      <React.Fragment key={node.id}>
        <tr className={`hover:bg-gray-50 ${level > 0 ? 'bg-gray-50/50' : ''}`}>
          <td className="p-3 whitespace-nowrap" style={{ paddingLeft: `${paddingLeft}rem` }}>
            <div className="flex items-center">
              {hasChildren && (
                <button
                  onClick={() => toggleNode(node.id)}
                  className="mr-2 p-1 hover:bg-gray-100 rounded"
                  aria-label={isExpanded ? 'Collapse node' : 'Expand node'}
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              )}
              <span className="font-medium text-gray-900">{node.sponsorCode}</span>
            </div>
          </td>
          <td className="p-3 whitespace-nowrap text-gray-900">{node.name}</td>
          <td className="p-3 whitespace-nowrap">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {node.rank}
            </span>
          </td>
          <td className="p-3 whitespace-nowrap text-gray-500">{node.phone}</td>
          <td className="p-3 whitespace-nowrap text-gray-500">{node.city}</td>
          <td className="p-3 whitespace-nowrap text-gray-500">{node.email}</td>
        </tr>
        {isExpanded && node.children?.map((child) => renderNetworkRow(child, level + 1))}
      </React.Fragment>
    );
  };

  const hasSearchResults = (node) => node && matchesSearch(node, searchTerm);

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Network Explorer</h1>
          <p className="text-gray-500">View and manage your MLM network structure</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-2"></div>
          <span>Loading network data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Network Explorer</h1>
          <p className="text-gray-500">View and manage your MLM network structure</p>
        </div>
        <div className="p-4 bg-red-50 rounded-lg text-red-600">
          <div className="font-medium">Error loading network data</div>
          <div className="text-sm mt-1">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-xl p-6 space-y-6 border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-400">
              Network Explorer
            </h1>
            <p className="text-sm text-gray-500 mt-1">Visualize and manage your network connections</p>
          </div>
        </div>

        {/* Search Box */}
        <div className="mb-6">
          <div className="flex justify-between items-center gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by name, sponsor code, rank, email, phone, or city..."
                className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              onClick={toggleAll}
              className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 border border-blue-200 flex items-center gap-2"
              aria-label={allExpanded ? 'Collapse all nodes' : 'Expand all nodes'}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {allExpanded ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                )}
              </svg>
              <span>{allExpanded ? 'Collapse All' : 'Expand All'}</span>
            </button>
          </div>
        </div>

        {/* Network Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="text-sm font-medium text-gray-500 mb-1">Total Members</div>
            <div className="text-2xl font-semibold text-gray-900">{networkStats.totalMembers}</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="text-sm font-medium text-gray-500 mb-1">Direct Referrals</div>
            <div className="text-2xl font-semibold text-gray-900">{networkStats.directReferrals}</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="text-sm font-medium text-gray-500 mb-1">Your Sponsor</div>
            <div className="text-2xl font-semibold text-gray-900">{networkStats.yourSponsor}</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="text-sm font-medium text-gray-500 mb-1">Your Rank</div>
            <div className="text-2xl font-semibold text-blue-600">{networkStats.yourRank}</div>
          </div>
        </div>

        {/* Network Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="p-3 text-left text-sm font-medium text-gray-500"
                  aria-label="Sponsor Code"
                >
                  Sponsor Code
                </th>
                <th
                  scope="col"
                  className="p-3 text-left text-sm font-medium text-gray-500"
                  aria-label="Name"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="p-3 text-left text-sm font-medium text-gray-500"
                  aria-label="Rank"
                >
                  Rank
                </th>
                <th
                  scope="col"
                  className="p-3 text-left text-sm font-medium text-gray-500"
                  aria-label="Phone"
                >
                  Phone
                </th>
                <th
                  scope="col"
                  className="p-3 text-left text-sm font-medium text-gray-500"
                  aria-label="City"
                >
                  City
                </th>
                <th
                  scope="col"
                  className="p-3 text-left text-sm font-medium text-gray-500"
                  aria-label="Email"
                >
                  Email
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {networkData && renderNetworkRow(networkData)}
            </tbody>
          </table>

          {/* No Results Message */}
          {networkData && searchTerm && !hasSearchResults(networkData) && (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <h3 className="text-gray-600 font-medium">No matches found</h3>
              <p className="text-gray-500 text-sm mt-1">Try adjusting your search query</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetworkExplorer;